<template>
  <div class="container-fluid saved-dashboard-page">
    <div class="saved-dashboard-hero mb-4">
      <div>
        <h4 class="mb-1">Saved Dashboards</h4>
        <p class="mb-0 text-muted">Manage private dashboards, public dashboards, and map visualizations.</p>
      </div>
      <b-button @click="$router.push('/custom')" variant="primary" class="saved-dashboard-add-btn">
        Add New
      </b-button>
    </div>

    <div v-if="loading" class="saved-dashboard-loading shadow-sm">
      <b-spinner small type="grow" variant="primary" class="mr-2"></b-spinner>
      <span>Refreshing your dashboards...</span>
    </div>

    <div
      v-else-if="privateDashboards.length === 0 && privateMapDashboards.length === 0 && publicDashboards.length === 0 && publicMapDashboards.length === 0"
      class="saved-dashboard-empty shadow-sm text-center"
    >
      <small>You have no saved dashboards yet. Create one to see it here automatically.</small>
    </div>

    <section v-if="privateDashboards.length" class="saved-dashboard-section">
      <h5 class="saved-dashboard-section-title">Your Private Dashboards</h5>
      <b-list-group class="saved-dashboard-list">
      <b-list-group-item
        v-for="dashboard in privateDashboards"
        :key="dashboard.id"
        href="#"
        class="saved-dashboard-item flex-column align-items-start"
        @click="loadDashboard(dashboard)"
      >
        <div class="d-flex w-100 justify-content-between">
          <div @click="copy(`${dashboard.link}${dashboard.id}`, $event)" class="mb-1">
            <strong class="text-primary mr-2">{{ dashboard.config.dashboardDetails.name }}</strong>
            <b-button-group size="xs">
              <b-button @click="edit($event, dashboard)" class="py-1" variant="info">
                <b-icon icon="pencil"></b-icon>
              </b-button>
              <b-button @click="remove($event, dashboard)" class="py-1" variant="danger">
                <b-icon icon="trash-fill"></b-icon>
              </b-button>
            </b-button-group>
          </div>
          <small>{{ moment(dashboard.lastEdited || dashboard.created).fromNow() }}</small>
        </div>
        <p class="m-0 p-0">{{ dashboard.config.dashboardDetails.description }}</p>
      </b-list-group-item>
      </b-list-group>
    </section>

    <section v-if="privateMapDashboards.length" class="saved-dashboard-section">
      <h5 class="saved-dashboard-section-title">Your Private Map Visualizations</h5>
      <b-list-group class="saved-dashboard-list">
      <b-list-group-item
        v-for="dashboard in privateMapDashboards"
        :key="dashboard.id"
        href="#"
        class="saved-dashboard-item saved-dashboard-item--map flex-column align-items-start"
        @click="loadDashboard(dashboard)"
      >
        <div class="d-flex w-100 justify-content-between">
          <div @click="copy(`${dashboard.link}${dashboard.id}`, $event)" class="mb-1">
            <strong class="text-primary mr-2">{{ dashboard.config.dashboardDetails.name }}</strong>
            <b-button-group size="xs">
              <b-button @click="edit($event, dashboard)" class="py-1" variant="info">
                <b-icon icon="pencil"></b-icon>
              </b-button>
              <b-button @click="remove($event, dashboard)" class="py-1" variant="danger">
                <b-icon icon="trash-fill"></b-icon>
              </b-button>
            </b-button-group>
          </div>
          <small>{{ moment(dashboard.lastEdited || dashboard.created).fromNow() }}</small>
        </div>
        <p class="m-0 p-0">{{ dashboard.config.dashboardDetails.description }}</p>
      </b-list-group-item>
      </b-list-group>
    </section>

    <section v-if="publicDashboards.length" class="saved-dashboard-section">
      <h5 class="saved-dashboard-section-title">Public Dashboards</h5>
      <b-list-group class="saved-dashboard-list">
      <b-list-group-item
        v-for="dashboard in publicDashboards"
        :key="dashboard.id"
        href="#"
        class="saved-dashboard-item flex-column align-items-start"
        @click="open(dashboard.link)"
      >
        <div class="d-flex w-100 justify-content-between">
          <div @click="copy(`${dashboard.link}${dashboard.id}`, $event)" class="mb-1">
            <strong class="text-primary mr-2">{{ dashboard.name_of_dashboard }}</strong>
            <b-button-group size="xs">
              <b-button class="py-1" variant="info">Copy Link</b-button>
            </b-button-group>
          </div>
          <small>{{
            dashboard.isConfirmed
              ? 'Approved'
              : dashboard.disapproved
              ? 'Disapproved'
              : 'Awaiting Approval'
          }}</small>
        </div>
        <p class="m-0 p-0">{{ dashboard.description }}</p>
      </b-list-group-item>
      </b-list-group>
    </section>

    <section v-if="publicMapDashboards.length" class="saved-dashboard-section">
      <h5 class="saved-dashboard-section-title">Public Map Visualizations</h5>
      <b-list-group class="saved-dashboard-list">
      <b-list-group-item
        v-for="dashboard in publicMapDashboards"
        :key="dashboard.id"
        href="#"
        class="saved-dashboard-item saved-dashboard-item--map flex-column align-items-start"
        @click="open(dashboard.link)"
      >
        <div class="d-flex w-100 justify-content-between">
          <div @click="copy(`${dashboard.link}${dashboard.id}`, $event)" class="mb-1">
            <strong class="text-primary mr-2">{{ dashboard.name_of_dashboard }}</strong>
            <b-button-group size="xs">
              <b-button class="py-1" variant="info">Copy Link</b-button>
            </b-button-group>
          </div>
          <small>{{
            dashboard.isConfirmed
              ? 'Approved'
              : dashboard.disapproved
              ? 'Disapproved'
              : 'Awaiting Approval'
          }}</small>
        </div>
        <p class="m-0 p-0">{{ dashboard.description }}</p>
      </b-list-group-item>
      </b-list-group>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
// import ApiServices from '@/modules/data-layer/services/ApiServices';

export default {
  data() {
    return {
      customDashboardsList: JSON.parse(
        localStorage.getItem('customDashboardsList') || JSON.stringify({}),
      ),
      publicDashboards: [],
      publicMapDashboards: [],
      privateDashboards: [],
      privateMapDashboards: [],
      loading: true,
    };
  },
  computed: {
    ...mapGetters('AUTH_STORE', ['getUser']),
    list() {
      return this.customDashboardsList[this.getUser.username] || [];
    },
  },
  methods: {
    moment,
    open(url) {
      window.open(url);
    },
    copy(url, e) {
      e.stopPropagation();
      e.preventDefault();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url);
        this.$swal.fire('URL copied to clipboard!');
      } else {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        this.$swal.fire('URL copied to clipboard!');
      }
    },
    refreshDashboards() {
      this.loading = true;
      // Load from local storage
      const localPrivateDashboards = [...this.list];
      this.privateDashboards = localPrivateDashboards.filter(
        (dashboard) => !this.isMapDashboard(dashboard),
      );
      this.privateMapDashboards = localPrivateDashboards.filter((dashboard) => this.isMapDashboard(dashboard));

      // Load from backend
      this.$store
        .dispatch('getDashboards')
        .then(({ result }) => {
          console.log(result, '@@@@TY@@@@@ 2');
          const backendPrivate = result
            .filter((req) => req.email === this.getUser.email && req.is_private === true)
            .map((req) => ({
              ...req,
              config: { ...JSON.parse(req.config) },
            }));

          // Combine local and backend private dashboards, avoid duplicates by tracking IDs
          const allPrivate = [...this.list];
          backendPrivate.forEach((bp) => {
            if (!allPrivate.find((p) => p.id === bp.id)) {
              allPrivate.push(bp);
            }
          });
          this.privateDashboards = allPrivate.filter(
            (dashboard) => !this.isMapDashboard(dashboard),
          );
          this.privateMapDashboards = allPrivate.filter((dashboard) => this.isMapDashboard(dashboard));

          const allPublic = result
            .filter((req) => req.email === this.getUser.email && req.is_private === false)
            .map((req) => ({
              ...req,
              config: { ...JSON.parse(req.config) },
            }));
          this.publicDashboards = allPublic.filter((dashboard) => !this.isMapDashboard(dashboard));
          this.publicMapDashboards = allPublic.filter((dashboard) => this.isMapDashboard(dashboard));
          this.loading = false;
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
          this.$swal.fire(
            'Could not retrieve your public dashboards from the server. Your local dashboards are still available.',
          );
        });
    },
    load(dashboard) {
      const {
        dashboardDetails, composedData, surveyArray, sectionsArray,
      } = dashboard.config;

      this.$store.dispatch('resetState');
      this.$store.dispatch('dashboardConfiguration', dashboardDetails);
      this.$store.dispatch('customDashboard', true);

      this.$store.commit('setPArea', composedData);
      this.$store.commit('setDArea', surveyArray);
      this.$store.commit('arrangedSections', sectionsArray);
      this.$store.commit('setEmbedUrl', dashboard.embedded_url);
      this.$store.commit('setEmbedIframe', dashboard.embedded_iframe);
    },
    loadDashboard(dashboard) {
      this.load(dashboard);

      const t = dashboard.config.dashboardDetails.name.replace(/\s+/g, '_').toLowerCase();
      this.$router.push({
        path: `/dashboard/${t}`,
        component: () => import('@/modules/dynamic-dashboard/index.vue'),
      });
    },
    isMapDashboard(dashboard) {
      const sections = dashboard?.config?.sectionsArray;
      if (!Array.isArray(sections)) return false;
      const sectionNames = sections.map((section) => (section?.name || section?.fieldName || '').toLowerCase().trim());
      return sectionNames.length === 1 && sectionNames[0] === 'map visualization';
    },
    edit(e, dashboard) {
      e.stopPropagation();
      this.load(dashboard);
      this.$store.commit('startEdit');
      this.$router.push('/my-dashboard/details');
    },
    remove(e, dashboard) {
      e.stopPropagation();
      this.$swal
        .fire({
          title: 'Confirm delete Dashboard',
          showDenyButton: true,
          denyButtonText: 'Cancel',
        })
        .then((result) => {
          if (result.isConfirmed) {
            const newList = this.list.filter((dashb) => dashb.id !== dashboard.id);
            this.customDashboardsList = {
              ...this.customDashboardsList,
              [this.getUser.username]: newList,
            };
            localStorage.setItem('customDashboardsList', JSON.stringify(this.customDashboardsList));
          }
        });
    },
  },
  async mounted() {
    this.refreshDashboards();

    // Watch for route changes to refresh when navigating to saved dashboards
    this.$watch('$route.hash', (newHash) => {
      if (newHash === '/savedDashboards') {
        this.refreshDashboards();
      }
    });
  },
  activated() {
    // Called when component becomes active (especially with keep-alive)
    this.refreshDashboards();
  },
};
</script>

<style scoped>
.saved-dashboard-page {
  padding-top: 1rem;
  padding-bottom: 2rem;
}

.saved-dashboard-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 18px;
  background: linear-gradient(135deg, #eff8fa 0%, #ffffff 100%);
  border: 1px solid #d6ecef;
}

.saved-dashboard-hero h4 {
  font-weight: 700;
  color: #0b4f5d;
}

.saved-dashboard-add-btn {
  white-space: nowrap;
}

.saved-dashboard-loading,
.saved-dashboard-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e7eef1;
  color: #3d5560;
  margin-bottom: 1rem;
}

.saved-dashboard-section {
  margin-top: 1rem;
}

.saved-dashboard-section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #214b57;
  margin: 0 0 0.75rem;
}

.saved-dashboard-list {
  display: grid;
  gap: 0.75rem;
}

.saved-dashboard-item {
  border: 1px solid #dbe7ea;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 10px 24px rgba(8, 52, 61, 0.04);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.saved-dashboard-item:hover {
  transform: translateY(-1px);
  border-color: #b8d9df;
  box-shadow: 0 14px 28px rgba(8, 52, 61, 0.08);
}

.saved-dashboard-item--map {
  background: linear-gradient(135deg, rgba(63, 137, 148, 0.08), rgba(255, 255, 255, 1));
}
</style>
